// Частта от намереното на продукт, която е в приготвените рецепти — на
// продуктовия ред (решетка и карти):
//   • чипът „+0,630 кг в рецепти" под полето „Намерено": полето е НАСИПНОТО, а
//     разликата, стойността и статусът са по насипно + в рецепти. Задържане на
//     мишката показва разбивката (title); клик / Enter / Space (чипът е бутон —
//     Tab от „Намерено" стига до него) отваря подреда с разбивката;
//   • подредът „В приготвени рецепти: Шопска салата × 3 — 0,630 кг · … · Общо
//     намерено 1,830 кг";
//   • подсказката „преброй и извън рецептата (или 0)" под името на съставка без
//     насипно преброено — иначе целият ѝ насипен запас става липса.
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { formatQtyWithUnit, type StocktakeComputedRow } from "./stocktakeWorksheetShared";
import { formatPortionsInput } from "./stocktakeRecipes";

// „Шопска салата × 3 — 0,630 кг · Овчарска салата × 1 — 0,210 кг"
const inRecipesPartsText = (row: StocktakeComputedRow, t: TFunction) =>
  row.recipeParts
    .map((part) =>
      t("inventoryPage.sr6InRecipesPart", {
        recipe: part.recipeName,
        portions: formatPortionsInput(part.portions),
        qty: formatQtyWithUnit(part.quantity, row.decimals, row.unit),
      }),
    )
    .join(" · ");

// „Общо намерено 1,830 кг" — само когато насипното е написано
const totalText = (row: StocktakeComputedRow, t: TFunction) =>
  row.countedQty != null
    ? t("inventoryPage.sr6InRecipesTotal", {
        total: formatQtyWithUnit(row.countedQty, row.decimals, row.unit),
      })
    : t("inventoryPage.sr6CountLooseHint");

type ChipProps = {
  row: StocktakeComputedRow;
  open: boolean;
  onToggle: () => void;
  // по-едър на картите (докосване с пръст)
  large?: boolean;
};

export const InRecipesChip = ({ row, open, onToggle, large }: ChipProps) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-expanded={open}
      title={`${t("inventoryPage.sr6InRecipesTitle")}: ${inRecipesPartsText(row, t)}`}
      onClick={onToggle}
      className={`inline-flex max-w-full cursor-pointer items-center gap-1 self-start truncate rounded-full border border-brand-tint2 bg-brand-tint px-[7px] font-semibold leading-[normal] text-brand-deep-ink outline-none hover:border-brand/45 focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
        large ? "py-1 text-[12.5px]" : "pb-[2px] pt-px text-[11px]"
      }`}
    >
      {t("inventoryPage.sr6InRecipesChip", {
        qty: formatQtyWithUnit(row.inRecipesQty, row.decimals, row.unit),
      })}
      <span aria-hidden className="text-[9px] opacity-70">
        {open ? "▴" : "▾"}
      </span>
    </button>
  );
};

// Подредът с разбивката (решетката го отмества като подреда „партида / срок")
export const InRecipesBreakdown = ({ row, className }: { row: StocktakeComputedRow; className: string }) => {
  const { t } = useTranslation();
  return (
    <div className={`text-[12px] leading-[1.45] text-body ${className}`}>
      <span className="font-semibold text-brand-deep-ink">{t("inventoryPage.sr6InRecipesTitle")}:</span>{" "}
      {inRecipesPartsText(row, t)}
      <span className="text-muted"> · {totalText(row, t)}</span>
    </div>
  );
};

// Под името на съставка без насипно преброено
export const CountLooseHint = () => {
  const { t } = useTranslation();
  return (
    <span className="truncate text-[11px] font-semibold leading-[13px] text-brand-deep-ink">
      {t("inventoryPage.sr6CountLooseHint")}
    </span>
  );
};
